import axios from "axios";
import {getCookie, setCookie} from "@/shared/lib/utils/cookie";

declare module 'axios' {
    interface AxiosRequestConfig {
        /**
         * Запрос к документу, открытому по внешней ссылке (с hash). 401 по такому запросу
         * означает «нет доступа к этому документу», а не «сессия истекла», — разлогинивать
         * по нему нельзя, иначе чужая ссылка выкидывает авторизованного пользователя.
         */
        publicLink?: boolean;
    }
}

// Доступ к защищённому паролем документу бэк выдаёт сессионной кукой: её ставит
// check-password, и она же нужна на всех остальных защищённых эндпоинтах. Без
// withCredentials браузер не сохранит Set-Cookie и не отправит куку обратно.
export const request = axios.create({
    withCredentials: true,
});

/**
 * Клиент для запросов без Bearer-токена: по внешней ссылке (с hash) и до авторизации.
 * От `request` отличается только отсутствием заголовка — куки нужны так же.
 */
export const publicRequest = axios.create({
    withCredentials: true,
});

let unauthorizedHandler: () => void | Promise<void> = () => {};

/**
 * Что делать, когда сессию восстановить не удалось (обычно — разлогинить).
 * shared не знает о пользователе, поэтому обработчик регистрирует слой app при старте.
 */
export const setUnauthorizedHandler = (handler: () => void | Promise<void>) => {
    unauthorizedHandler = handler;
};

if (import.meta.env.DEV) {
    // Подмена сбоев цепочки подписания для ручной проверки; в production-сборку не попадает.
    // Публичный инстанс тоже нужен: запросы по внешней ссылке (с hash) идут мимо request.
    import('./signFaultDev').then(({installSignFaultInterceptor}) => {
        installSignFaultInterceptor(request);
        installSignFaultInterceptor(publicRequest);
    });
}

request.interceptors.request.use((config) => {
    const token = getCookie('GATEWAY') ?? '';
    if (token) {
        config.headers['Authorization'] = 'Bearer ' + token;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

interface QueueItem {
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

// HASH_SESSION ставит бэк на своём домене, на localhost её не бывает,
// поэтому в DEV источником обновления служит refresh_token из localStorage
const hasRefreshSource = () => Boolean(
    getCookie('HASH_SESSION') || (import.meta.env.DEV && localStorage.getItem('refresh_token'))
);

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

request.interceptors.response.use(async (response) => {
        if (typeof response.data === 'string' && response.data.includes('Request Rejected')) {
            const match = response.data.match(/Your support ID is:\s*(\d+)/);
            const supportId = match ? match[1] : null;

            if (supportId) {
                axios.post('/proxy/tracking-action.bpmn/api/v1/event/tb/send', {
                    message: `Your support ID is: ${supportId}. Path: ${response.config?.url}`,
                    type: "DAW"
                }).catch(err => console.error('Не удалось отправить Support ID в трекинг:', err));
            }

            // Sentry подгружается лениво, чтобы не тянуть его в главный чанк
            import('@sentry/vue').then((Sentry) => {
                Sentry.captureMessage(`Request Rejected by WAF: ${response.config?.url}`, {
                    level: 'error',
                    extra: {url: response.config?.url, supportId}
                });
            }).catch(() => {});

            const customError = new Error('Request Rejected by WAF') as any;
            customError.response = response;
            customError.statusCode = 403;

            return Promise.reject(customError);
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry && hasRefreshSource()) {
            if (isRefreshing) {
                // Если обновление токена уже идет, ставим запрос в очередь
                return new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                }).then(() => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + getCookie('GATEWAY');
                    return request(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const data = (await axios.post('/proxy/account-recovery-api.bpmn/auth/login/refresh/token', {
                    "refreshToken": import.meta.env.DEV ? localStorage.getItem('refresh_token') : undefined
                })).data;
                if (import.meta.env.DEV) {
                    setCookie('GATEWAY', data.access_token, 1, '/');
                    localStorage.setItem('refresh_token', data.refresh_token)
                }
                const newToken = data.access_token;
                request.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
                processQueue(null, newToken);
                originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                return request(originalRequest);
            } catch (err) {
                await unauthorizedHandler();
                processQueue(err, null);
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        } else if (error.response?.status === 401 && !hasRefreshSource() && getCookie('GATEWAY') && !originalRequest?.publicLink) {
            // Обновить токен нечем, но сессия числится активной — разлогиниваем,
            // чтобы isAuth упал в false и открылось окно авторизации
            await unauthorizedHandler();
            processQueue(error, null);
        }

        return Promise.reject(error);
    });
